/**
 * HCX — Quote Button Scroll + Form Pre-Population
 * 
 * This script handles the "Request a Custom Quote" button behavior:
 * 1. Smooth scrolls to the quote form (#request-quote)
 * 2. Pre-populates the form with product, variant, and upgrade options
 * 3. Focuses the first input field
 * 
 * INSTALLATION:
 * Add this script to your theme.liquid before </body> or as a separate JS file
 * loaded on product pages.
 * 
 * REQUIREMENTS:
 * - Quote button must link to "#request-quote" or have data-action="request-quote"
 * - Contact form must have an anchor #request-quote above it
 * - Form fields identified by common selectors (adjust as needed for your Klaviyo form)
 */

(function() {
  'use strict';

  // Configuration - adjust these selectors to match your theme/form
  var CONFIG = {
    // Button selector - the "Request a Custom Quote" button
    quoteButtonSelector: 'a[href="#request-quote"], a[href*="request-quote"], button[data-action="request-quote"], .product-form__buttons a:contains("Quote")',
    
    // Form anchor - where to scroll to
    formAnchor: '#request-quote',
    
    // Form selectors - adjust to match your Klaviyo form field names/IDs
    formSelectors: {
      name: 'input[name="name"], input[name="Name"], input[placeholder*="Name"]',
      email: 'input[name="email"], input[name="Email"], input[type="email"]',
      message: 'textarea[name="message"], textarea[name="Message"], textarea[placeholder*="Message"]',
      companyType: 'select[name="company_type"], select[name="Company Type"]',
      inquiryType: 'select[name="inquiry_type"], select[name="Inquiry Type"]'
    },
    
    // Scroll behavior
    scrollOffset: 20, // Pixels from top after scroll
    scrollBehavior: 'smooth'
  };

  /**
   * Get the currently selected variant info
   */
  function getSelectedVariant() {
    var info = {
      title: '',
      sku: '',
      price: ''
    };

    // Try to get from variant selector (common patterns)
    var variantSelect = document.querySelector('select[name="id"], .product-variant-picker select, [data-variant-select]');
    if (variantSelect && variantSelect.options[variantSelect.selectedIndex]) {
      info.title = variantSelect.options[variantSelect.selectedIndex].text;
    }

    // Try to get from radio buttons (if using that pattern)
    var checkedRadio = document.querySelector('input[name="id"]:checked, .variant-input:checked');
    if (checkedRadio) {
      var label = document.querySelector('label[for="' + checkedRadio.id + '"]');
      if (label) info.title = label.textContent.trim();
    }

    // Try to get SKU
    var skuElement = document.querySelector('.product__sku-value, [data-sku], .sku-value');
    if (skuElement) {
      info.sku = skuElement.textContent.trim();
    }

    // Try to get price
    var priceElement = document.querySelector('.product__price .money, .price .money, [data-product-price]');
    if (priceElement) {
      info.price = priceElement.textContent.trim();
    }

    return info;
  }

  /**
   * Get selected upgrade options (from Optis or similar apps)
   */
  function getUpgradeOptions() {
    var options = [];

    // Look for Optis product options
    var optisSelects = document.querySelectorAll('[class*="optis"] select, [data-optis] select');
    optisSelects.forEach(function(select) {
      if (select.value) {
        var label = '';
        var labelEl = select.closest('.form-field, .option-group')?.querySelector('label');
        if (labelEl) label = labelEl.textContent.trim().replace(':', '');
        options.push({
          name: label || select.name,
          value: select.options[select.selectedIndex].text
        });
      }
    });

    // Look for custom option dropdowns (generic pattern)
    var customSelects = document.querySelectorAll('.product-form select:not([name="id"])');
    customSelects.forEach(function(select) {
      if (select.value && !select.closest('[class*="optis"]')) {
        var label = '';
        var labelEl = select.closest('.form-field')?.querySelector('label');
        if (labelEl) label = labelEl.textContent.trim().replace(':', '');
        options.push({
          name: label || select.name,
          value: select.options[select.selectedIndex].text
        });
      }
    });

    return options;
  }

  /**
   * Get product title
   */
  function getProductTitle() {
    var titleEl = document.querySelector('.product__title h1, h1.product-title, [data-product-title]');
    return titleEl ? titleEl.textContent.trim() : '';
  }

  /**
   * Build the pre-populated message
   */
  function buildMessage() {
    var lines = [];
    var productTitle = getProductTitle();
    var variant = getSelectedVariant();
    var upgrades = getUpgradeOptions();

    lines.push('--- Product Interest ---');
    
    if (productTitle) {
      lines.push('Product: ' + productTitle);
    }

    if (variant.title) {
      lines.push('Model/Variant: ' + variant.title);
    }

    if (variant.sku) {
      lines.push('SKU: ' + variant.sku);
    }

    if (variant.price && variant.price !== '$0.00') {
      lines.push('Listed Price: ' + variant.price);
    }

    if (upgrades.length > 0) {
      lines.push('');
      lines.push('Selected Options:');
      upgrades.forEach(function(opt) {
        lines.push('  • ' + opt.name + ': ' + opt.value);
      });
    }

    lines.push('');
    lines.push('--- Your Message ---');
    lines.push('');

    return lines.join('\n');
  }

  /**
   * Pre-populate the form
   */
  function prePopulateForm() {
    var selectors = CONFIG.formSelectors;

    // Pre-populate message field
    var messageField = document.querySelector(selectors.message);
    if (messageField) {
      var existingMessage = messageField.value.trim();
      var prePopulated = buildMessage();
      
      // Only pre-populate if empty or already has our header
      if (!existingMessage || existingMessage.includes('--- Product Interest ---')) {
        messageField.value = prePopulated;
      }
    }

    // Set inquiry type to "Quote Request" if available
    var inquirySelect = document.querySelector(selectors.inquiryType);
    if (inquirySelect) {
      for (var i = 0; i < inquirySelect.options.length; i++) {
        var optText = inquirySelect.options[i].text.toLowerCase();
        if (optText.includes('quote') || optText.includes('pricing')) {
          inquirySelect.selectedIndex = i;
          break;
        }
      }
    }

    // Focus the name field (or email if name not found)
    var nameField = document.querySelector(selectors.name);
    var emailField = document.querySelector(selectors.email);
    
    setTimeout(function() {
      if (nameField && !nameField.value) {
        nameField.focus();
      } else if (emailField && !emailField.value) {
        emailField.focus();
      }
    }, 100);
  }

  /**
   * Smooth scroll to form
   */
  function scrollToForm() {
    var target = document.querySelector(CONFIG.formAnchor);
    if (!target) {
      console.warn('[HCX Quote] Form anchor not found:', CONFIG.formAnchor);
      return false;
    }

    var targetPosition = target.getBoundingClientRect().top + window.pageYOffset - CONFIG.scrollOffset;
    
    window.scrollTo({
      top: targetPosition,
      behavior: CONFIG.scrollBehavior
    });

    return true;
  }

  /**
   * Handle quote button click
   */
  function handleQuoteClick(e) {
    e.preventDefault();
    
    if (scrollToForm()) {
      // Small delay to let scroll complete before pre-populating
      setTimeout(prePopulateForm, 300);
    }
  }

  /**
   * Initialize - find and bind quote buttons
   */
  function init() {
    // Find quote buttons using multiple selector patterns
    var buttons = [];
    
    // Direct href match
    document.querySelectorAll('a[href="#request-quote"]').forEach(function(el) {
      buttons.push(el);
    });

    // Button with quote-related text
    document.querySelectorAll('.product-form a, .product-form button').forEach(function(el) {
      var text = el.textContent.toLowerCase();
      if (text.includes('quote') && !buttons.includes(el)) {
        buttons.push(el);
      }
    });

    // Data attribute
    document.querySelectorAll('[data-action="request-quote"]').forEach(function(el) {
      if (!buttons.includes(el)) buttons.push(el);
    });

    if (buttons.length === 0) {
      // Don't warn - might be on a non-product page
      return;
    }

    buttons.forEach(function(btn) {
      btn.addEventListener('click', handleQuoteClick);
    });

    console.log('[HCX Quote] Initialized with', buttons.length, 'quote button(s)');
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Also run on Shopify section render (for theme editor)
  document.addEventListener('shopify:section:load', init);

})();
/**
 * HCX — Sticky Mobile Add-to-Cart Bar
 * 
 * Shows a sticky bar at bottom of screen on mobile when user scrolls
 * past the main buy buttons. Provides quick access to Add to Cart
 * and Request Quote actions.
 * 
 * INSTALLATION:
 * 1. Add the CSS from hcx-mobile-optimizations.css
 * 2. Add this script before </body> in theme.liquid
 * 3. The HTML is injected automatically by this script
 */

(function() {
  'use strict';

  // Only run on mobile
  if (window.innerWidth > 768) return;

  // Only run on product pages
  if (!document.querySelector('.product-form, [data-product-form]')) return;

  // Configuration
  var CONFIG = {
    triggerSelector: '.product-form__buttons, .product__buttons, [data-buy-buttons]',
    stickyBarId: 'hcx-sticky-bar',
    visibleClass: 'is-visible',
    bodyClass: 'has-sticky-bar'
  };

  // Create sticky bar HTML
  function createStickyBar() {
    var bar = document.createElement('div');
    bar.id = CONFIG.stickyBarId;
    bar.className = 'hcx-sticky-bar';
    bar.innerHTML = [
      '<div class="hcx-sticky-bar__inner">',
      '  <button type="button" class="hcx-sticky-bar__btn hcx-sticky-bar__btn--primary" data-action="add-to-cart">',
      '    Add to Cart',
      '  </button>',
      '  <button type="button" class="hcx-sticky-bar__btn hcx-sticky-bar__btn--secondary" data-action="request-quote">',
      '    Get Quote',
      '  </button>',
      '</div>'
    ].join('');
    
    document.body.appendChild(bar);
    return bar;
  }

  // Handle sticky bar button clicks
  function bindStickyBarActions(bar) {
    var addToCartBtn = bar.querySelector('[data-action="add-to-cart"]');
    var quoteBtn = bar.querySelector('[data-action="request-quote"]');

    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', function() {
        // Find and click the main add to cart button
        var mainBtn = document.querySelector('.product-form__submit, button[name="add"], [data-add-to-cart]');
        if (mainBtn) {
          mainBtn.click();
        }
      });
    }

    if (quoteBtn) {
      quoteBtn.addEventListener('click', function() {
        // Scroll to quote form
        var quoteSection = document.querySelector('#request-quote');
        if (quoteSection) {
          quoteSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }
  }

  // Show/hide sticky bar based on scroll position
  function handleScroll(bar, trigger) {
    var triggerRect = trigger.getBoundingClientRect();
    var triggerBottom = triggerRect.bottom;

    // Show bar when trigger buttons are scrolled out of view
    if (triggerBottom < 0) {
      bar.classList.add(CONFIG.visibleClass);
      document.body.classList.add(CONFIG.bodyClass);
    } else {
      bar.classList.remove(CONFIG.visibleClass);
      document.body.classList.remove(CONFIG.bodyClass);
    }
  }

  // Initialize
  function init() {
    var trigger = document.querySelector(CONFIG.triggerSelector);
    if (!trigger) return;

    var bar = createStickyBar();
    bindStickyBarActions(bar);

    // Throttled scroll handler
    var ticking = false;
    window.addEventListener('scroll', function() {
      if (!ticking) {
        window.requestAnimationFrame(function() {
          handleScroll(bar, trigger);
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });

    // Initial check
    handleScroll(bar, trigger);
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();